import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Alert, Button, Card, CardContent, Typography } from '@mui/material';
import CardStyles from '../../styles/CardStyles';

import { ParentAffiliationService,
  ParentLocalGroupsService,
  ParentSchoolsService,
  ParentService,
  SavedSearchService,
  SitterNotificationSettings,
  SitterNotificationSettingsService,
  SitterService,
  UserService } from 'us-web-services';
import SettingToggle from '../common/SettingToggle';
import useUser from '../../store/useUser';
import DisplayService from '../../util/DisplayService';

interface Props {
  userId: number;
}

function UserNotifications(props: Props) {
  const { userId } = props;
  const { classes } = CardStyles();

  const [userState, userActions] = useUser();
  const [parentSavedSearches, setParentSavedSearches] = useState([]);
  const [sitterNotificationSettings, setSitterNotificationSettings] = useState<SitterNotificationSettings>({});

  const sitterNotifications = {
    availabilityRemindText: 'Availability reminder texts',
    interviewRequestText: 'Interview request texts',
    jobRequestText: 'Job request texts',
    weeklySummaryMail: 'Weekly summary emails',
  };

  const showError = error => {
    const displayedError = DisplayService.getErrorResponse(
      error,
      'There was an error updating the notification setting.',
    );

    toast.error(displayedError.message);
  };

  const toggleSavedSearchNotification = async (isChecked: boolean, id: number) => {
    const toggleParams = { id, notificationType: { id: isChecked ? 2 : 1 } };

    await SavedSearchService.patch(id, toggleParams);

    const searchParams = `?active=true&parentId=${userId}`;
    const searchesResponse = (await SavedSearchService.getByFilter(searchParams)).data;

    setParentSavedSearches(searchesResponse.data);
  };

  const toggleSitterNotificationSetting = async (isChecked: boolean, code: string) => {
    const params = { id: userId };

    params[code] = isChecked;

    const sitterSetting = (await SitterNotificationSettingsService.patch(userId, params)).data;

    setSitterNotificationSettings(sitterSetting.data);
  };

  const toggleFriendRecommendationEmail = async (isChecked: boolean, id: number) => {
    const params = {
      id,
      friendRecommendationId: isChecked ? 2 : 1,
    };

    const parentResponse = (await ParentService.patch(userId, params)).data;

    userActions.setProfile(parentResponse.data);
  };

  const toggleJobBoardEmail = async enabled => {
    const params = {
      id: userId,
      jobPostMessageEnabled: enabled,
    };

    const sitterResponse = await SitterService.patch(userId, params);
    const { data } = sitterResponse.data;

    userActions.setProfile(data);
  };

  const toggleNewsletterEmail = async (isChecked: boolean, id: number) => {
    try {
      const userResponse = await UserService.patch(id, {
        emailNewsletter: isChecked,
        id,
      });
      const { data } = userResponse.data;

      userActions.setUser(data);
    } catch (error) {
      showError(error);
    }
  };

  const toggleAffiliationNotification = async (enabled: boolean, affiliationId: number) => {
    try {
      await ParentAffiliationService.patch(affiliationId, {
        id: affiliationId,
        recommendationNotificationId: enabled ? 2 : 1,
      });

      const parentResponse = await ParentService.get(userId);
      const { data } = parentResponse.data;

      userActions.setProfile(data);
    } catch (error) {
      showError(error);
    }
  };

  const toggleLocalGroupNotification = async (enabled, localGroupId) => {
    try {
      await ParentLocalGroupsService.patch(localGroupId, {
        id: localGroupId,
        recommendationNotificationId: enabled ? 2 : 1,
      });

      const parentResponse = await ParentService.get(userId);
      const { data } = parentResponse.data;

      userActions.setProfile(data);
    } catch (error) {
      showError(error);
    }
  };

  const toggleSchoolNotification = async (isChecked: boolean, id: number) => {
    try {
      await ParentSchoolsService.patch(id, {
        id,
        recommendationNotificationId: isChecked ? 2 : 1,
      });

      const parentResponse = await ParentService.get(userId);
      const { data } = parentResponse.data;

      userActions.setProfile(data);
    } catch (error) {
      showError(error);
    }
  };

  const unsubscribeAll = async () => {
    try {
      if (userState.user.emailNewsletter) {
        toggleNewsletterEmail(false, userId);
      }

      if (userState.user.sitter) {
        if (userState.profile.jobPostMessageEnabled) {
          toggleJobBoardEmail(false);
        }

        if (sitterNotificationSettings.weeklySummaryMail) {
          toggleSitterNotificationSetting(false, 'weeklySummaryMail');
        }
      }

      if (userState.user.parent) {
        if (userState.profile.friendRecommendationId === 2) {
          toggleFriendRecommendationEmail(false, userId);
        }
        userState.profile.affiliations.map(affiliation => {
          if (affiliation.recommendationNotificationId === 2) {
            return toggleAffiliationNotification(false, affiliation.id);
          }

          return null;
        });
        userState.profile.localGroups.map(localGroup => {
          if (localGroup.recommendationNotificationId === 2) {
            return toggleLocalGroupNotification(false, localGroup.id);
          }

          return null;
        });
        userState.profile.schools.map(school => {
          if (school.recommendationNotificationId === 2) {
            return toggleSchoolNotification(false, school.id);
          }

          return null;
        });
        if (parentSavedSearches.length) {
          parentSavedSearches.map(search => {
            if (search.notificationType.id === 2) {
              return toggleSavedSearchNotification(false, search.id);
            }

            return null;
          });
        }
      }
    } catch (error) {
      const displayedError = DisplayService.getErrorResponse(
        error,
        'There was an error updating the user notification settings.',
      );

      toast.error(displayedError.message);
    }

    toast.success('All non-transactional user notifications disabled.');
  };

  useEffect(() => {
    const getParentSavedSearches = async () => {
      const params = `?active=true&parentId=${userId}`;
      const searchesResponse = await SavedSearchService.getByFilter(params);

      setParentSavedSearches(searchesResponse.data.data);
    };

    const getSitterNotificationSettings = async () => {
      const settingsResponse = (await SitterNotificationSettingsService.getById(userId)).data;

      setSitterNotificationSettings(settingsResponse.data);
    };

    if (userState.user.parent) {
      getParentSavedSearches();
    }

    if (userState.user.sitter) {
      getSitterNotificationSettings();
    }
  }, [
    setSitterNotificationSettings,
    userId,
    userState.user.id,
    userState.user.parent,
    userState.user.sitter,
  ]);

  const isBlocked = !userState.user.active;

  return (
    <Card className={classes.sectionCard}>
      <CardContent className={classes.sectionContent}>
        <Typography className={classes.sectionHeader}>
          User Notifications
        </Typography>
        {isBlocked && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            This user is blocked. Notification preferences are read-only and will not be delivered.
          </Alert>
        )}
        <Button
          id='unsubscribe-all'
          variant='outlined'
          color='error'
          size='small'
          onClick={unsubscribeAll}
          className={classes.actionButton}
          sx={{ mb: 2 }}
          disabled={isBlocked}
        >
          Unsubscribe All
        </Button>
        <SettingToggle
          id={userId}
          isChecked={userState.user.emailNewsletter || false}
          label='Newsletter emails'
          toggleFunction={toggleNewsletterEmail}
          disabled={isBlocked}
        />
        {userState.user.parent && (
          <SettingToggle
            id={userId}
            isChecked={userState.profile.friendRecommendationId === 2}
            label='Friend-recommended sitter emails'
            toggleFunction={toggleFriendRecommendationEmail}
            disabled={isBlocked}
          />
        )}
        {userState.user.parent &&
          userState.profile.affiliations &&
          userState.profile.affiliations.map(affiliation => (
            <SettingToggle
              id={affiliation.id}
              isChecked={
                affiliation.recommendationNotificationId === 2 || false
              }
              key={`affiliation-${affiliation.id}`}
              label={affiliation.affiliation.name}
              toggleFunction={toggleAffiliationNotification}
              disabled={isBlocked}
            />
          ))}
        {userState.user.parent &&
          userState.profile.localGroups &&
          userState.profile.localGroups.map(localGroup => (
            <SettingToggle
              id={localGroup.id}
              isChecked={localGroup.recommendationNotificationId === 2 || false}
              key={`localGroup-${localGroup.id}`}
              label={localGroup.localGroup.name}
              toggleFunction={toggleLocalGroupNotification}
              disabled={isBlocked}
            />
          ))}
        {userState.user.parent &&
          userState.profile.schools &&
          userState.profile.schools.map(school => (
            <SettingToggle
              id={school.id}
              isChecked={school.recommendationNotificationId === 2 || false}
              key={`school-${school.id}`}
              label={school.school.name}
              toggleFunction={toggleSchoolNotification}
              disabled={isBlocked}
            />
          ))}
        {userState.user.parent &&
          parentSavedSearches.length > 0 &&
          parentSavedSearches.map(search => (
            <SettingToggle
              id={search.id}
              isChecked={search.notificationType.id === 2 || false}
              key={`saved-search-${search.id}`}
              label={search.title}
              toggleFunction={toggleSavedSearchNotification}
              disabled={isBlocked}
            />
          ))}
        {userState.user.sitter && (
          <SettingToggle
            id='jobBoardEmail'
            isChecked={userState.profile.jobPostMessageEnabled || false}
            label='Job Board post alerts'
            toggleFunction={toggleJobBoardEmail}
            disabled={isBlocked}
          />
        )}
        {userState.user.sitter && (
          <SettingToggle
            id='weeklySummaryMail'
            isChecked={sitterNotificationSettings.weeklySummaryMail || false}
            label={sitterNotifications.weeklySummaryMail}
            toggleFunction={toggleSitterNotificationSetting}
            disabled={isBlocked}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default UserNotifications;
